<template>
  <div class="admin-dashboard">
    <!-- System Overview -->
    <div class="metrics-grid">
      <div class="metric-card">
        <div class="metric-icon">
          <md-icon>store</md-icon>
        </div>
        <div class="metric-content">
          <div class="metric-value">{{ formatNumber(totalVendors) }}</div>
          <div class="metric-label">Total Vendors</div>
        </div>
      </div>

      <div class="metric-card">
        <div class="metric-icon">
          <md-icon>business</md-icon>
        </div>
        <div class="metric-content">
          <div class="metric-value">{{ formatNumber(totalPorts) }}</div>
          <div class="metric-label">Ports</div>
        </div>
      </div>

      <div class="metric-card">
        <div class="metric-icon">
          <md-icon>account_balance</md-icon>
        </div>
        <div class="metric-content">
          <div class="metric-value">{{ formatNumber(totalDepartments) }}</div>
          <div class="metric-label">Departments</div>
        </div>
      </div>

      <div class="metric-card">
        <div class="metric-icon">
          <md-icon>people</md-icon>
        </div>
        <div class="metric-content">
          <div class="metric-value">{{ formatNumber(totalLabourers) }}</div>
          <div class="metric-label">Registered Labourers</div>
        </div>
      </div>
    </div>

    <!-- Performance Overview -->
    <div class="md-layout">
      <div class="md-layout-item md-size-50">
        <md-card>
          <md-card-header>
            <div class="card-title">Guidances by Port</div>
          </md-card-header>
          <md-card-content>
            <canvas ref="portChart"></canvas>
          </md-card-content>
        </md-card>
      </div>

      <div class="md-layout-item md-size-50">
        <md-card>
          <md-card-header>
            <div class="card-title">Top Performing Vendors</div>
          </md-card-header>
          <md-card-content>
            <canvas ref="vendorChart"></canvas>
          </md-card-content>
        </md-card>
      </div>
    </div>

    <!-- Vendor Performance Table -->
    <md-card>
      <md-card-header>
        <div class="card-title">Vendor Performance</div>
      </md-card-header>
      <md-card-content>
        <md-table>
          <md-table-row>
            <md-table-head>Vendor</md-table-head>
            <md-table-head md-numeric>Active Guidances</md-table-head>
            <md-table-head md-numeric>Total Labourers</md-table-head>
            <md-table-head md-numeric>Utilization Rate</md-table-head>
            <md-table-head md-numeric>Revenue (TZS)</md-table-head>
            <md-table-head>Rating</md-table-head>
          </md-table-row>
          <md-table-row v-for="vendor in vendorPerformance" :key="vendor.id">
            <md-table-cell>{{ vendor.name }}</md-table-cell>
            <md-table-cell md-numeric>{{ vendor.active_guidances }}</md-table-cell>
            <md-table-cell md-numeric>{{ vendor.total_labourers }}</md-table-cell>
            <md-table-cell md-numeric>
              <md-progress-bar :md-value="vendor.utilization" md-mode="determinate"></md-progress-bar>
              {{ vendor.utilization }}%
            </md-table-cell>
            <md-table-cell md-numeric>{{ formatCurrency(vendor.revenue) }}</md-table-cell>
            <md-table-cell>
              <div class="rating">
                <md-icon v-for="n in 5" :key="n" :class="{ active: n <= vendor.rating }">star</md-icon>
              </div>
            </md-table-cell>
          </md-table-row>
        </md-table>
      </md-card-content>
    </md-card>

    <!-- Exception Alerts -->
    <md-card class="alert-card" v-if="alerts.length">
      <md-card-header class="md-warning">
        <div class="card-title">
          <md-icon>warning</md-icon>
          Active Alerts
        </div>
      </md-card-header>
      <md-card-content>
        <div v-for="alert in alerts" :key="alert.id" class="alert-item">
          <div class="alert-icon">
            <md-icon :class="alert.type">{{ alert.icon }}</md-icon>
          </div>
          <div class="alert-content">
            <div class="alert-title">{{ alert.title }}</div>
            <div class="alert-message">{{ alert.message }}</div>
          </div>
          <md-button class="md-primary md-simple" @click="resolveAlert(alert.id)">
            Resolve
          </md-button>
        </div>
      </md-card-content>
    </md-card>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import { mapGetters } from 'vuex';

export default {
  name: 'AdminDashboard',
  props: {
    dateRange: {
      type: Object,
      default: function() {
        return { start: null, end: null };
      }
    }
  },
  data: function() {
    return {
      totalVendors: 0,
      totalPorts: 0,
      totalDepartments: 0,
      totalLabourers: 0,
      vendorPerformance: [],
      alerts: [],
      portChart: null,
      vendorChart: null
    };
  },
  computed: {
    ...mapGetters('dashboard', ['isLoading', 'adminStats']),
    me: function() {
      return this.$store.state.profile.me;
    }
  },
  created: function() {
    this.fetchAdminData();
  },
  watch: {
    dateRange: {
      handler: function() {
        this.fetchAdminData();
      },
      deep: true
    }
  },
  methods: {
    async fetchAdminData() {
      try {
        var params = {
          start_date: this.dateRange.start,
          end_date: this.dateRange.end
        };

        await this.$store.dispatch('dashboard/fetchAdminStats', params);
        var data = this.$store.getters['dashboard/adminStats'];

        if (data) {
          this.totalVendors = data.total_vendors || 0;
          this.totalPorts = data.total_ports || 0;
          this.totalDepartments = data.total_departments || 0;
          this.totalLabourers = data.total_labourers || 0;
          this.vendorPerformance = data.top_vendors || [];
          this.alerts = data.alerts || [];

          this.$nextTick(function() {
            this.initCharts(data);
          }.bind(this));
        }
      } catch (error) {
        console.error('Error fetching admin dashboard:', error);
        this.$store.dispatch('alerts/error', 'Failed to load admin dashboard data');
      }
    },

    initCharts: function(data) {
      this.initPortChart(data);
      this.initVendorChart(data);
    },

    initPortChart: function(data) {
      var ctx = this.$refs.portChart;
      if (!ctx) return;

      if (this.portChart) {
        this.portChart.destroy();
      }

      var canvasCtx = ctx.getContext('2d');

      this.portChart = new Chart(canvasCtx, {
        type: 'bar',
        data: {
          labels: data.port_labels || [],
          datasets: [{
            label: 'Number of Guidances',
            data: data.port_data || [],
            backgroundColor: '#9c27b0',
            borderRadius: 4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                stepSize: 1
              }
            }
          },
          plugins: {
            legend: {
              position: 'top'
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  return 'Guidances: ' + context.raw;
                }
              }
            }
          }
        }
      });
    },

    initVendorChart: function(data) {
      var ctx = this.$refs.vendorChart;
      if (!ctx) return;

      if (this.vendorChart) {
        this.vendorChart.destroy();
      }

      var canvasCtx = ctx.getContext('2d');
      var vendorNames = [];
      var vendorRevenues = [];

      if (this.vendorPerformance && this.vendorPerformance.length) {
        for (var i = 0; i < this.vendorPerformance.length; i++) {
          vendorNames.push(this.vendorPerformance[i].name);
          vendorRevenues.push(this.vendorPerformance[i].revenue / 1000000); // Convert to millions
        }
      }

      // For Chart.js v3, use bar chart with indexAxis: 'y' for horizontal bars
      this.vendorChart = new Chart(canvasCtx, {
        type: 'bar',
        data: {
          labels: vendorNames,
          datasets: [{
            label: 'Cost (Millions TZS)',
            data: vendorRevenues,
            backgroundColor: '#4caf50',
            borderRadius: 4
          }]
        },
        options: {
          indexAxis: 'y', // This creates horizontal bar chart (replaces horizontalBar)
          responsive: true,
          maintainAspectRatio: true,
          scales: {
            x: {
              beginAtZero: true,
              title: {
                display: true,
                text: 'Cost (Millions TZS)'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Vendors'
              }
            }
          },
          plugins: {
            legend: {
              position: 'top'
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  return 'Revenue: ' + (context.raw * 1000000).toLocaleString() + ' TZS';
                }
              }
            }
          }
        }
      });
    },

    async resolveAlert(alertId) {
      try {
        await this.$store.dispatch('dashboard/resolveAlert', alertId);
        await this.fetchAdminData();
        this.$store.dispatch('alerts/success', 'Alert resolved successfully');
      } catch (error) {
        console.error('Error resolving alert:', error);
        this.$store.dispatch('alerts/error', 'Failed to resolve alert');
      }
    },

    formatNumber: function(value) {
      if (!value) return '0';
      return new Intl.NumberFormat('en-US').format(value);
    },

    formatCurrency: function(value) {
      if (!value) return 'TZS 0';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TZS',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    }
  },

  beforeDestroy: function() {
    if (this.portChart) {
      this.portChart.destroy();
      this.portChart = null;
    }
    if (this.vendorChart) {
      this.vendorChart.destroy();
      this.vendorChart = null;
    }
  }
};
</script>

<style scoped>
.admin-dashboard {
  padding: 10px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 24px;
}

.metric-card {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
  border-radius: 10px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: transform 0.2s;
}

.metric-card:hover {
  transform: translateY(-2px);
}

.metric-card .metric-icon md-icon {
  font-size: 36px;
}

.metric-card.success .metric-icon md-icon {
  color: #4caf50;
}

.metric-card.warning .metric-icon md-icon {
  color: #ff9800;
}

.metric-card.info .metric-icon md-icon {
  color: #2196f3;
}

.metric-content {
  flex: 1;
}

.metric-value {
  font-size: 24px;
  font-weight: bold;
  color: #333;
}

.metric-label {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.card-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
}

.rating {
  display: flex;
  gap: 2px;
}

.rating md-icon {
  font-size: 18px;
  color: #ddd;
}

.rating md-icon.active {
  color: #ffc107;
}

.alert-card {
  margin-top: 20px;
}

.alert-item {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 12px;
  border-bottom: 1px solid #f0f0f0;
}

.alert-item:last-child {
  border-bottom: none;
}

.alert-icon md-icon {
  font-size: 24px;
}

.alert-icon md-icon.error {
  color: #f44336;
}

.alert-icon md-icon.warning {
  color: #ff9800;
}

.alert-icon md-icon.info {
  color: #2196f3;
}

.alert-content {
  flex: 1;
}

.alert-title {
  font-weight: 500;
  font-size: 14px;
}

.alert-message {
  font-size: 12px;
  color: #666;
}

/* Chart container styles */
canvas {
  max-height: 300px;
  width: 100%;
}

/* Progress bar */
.md-progress-bar {
  width: 80px;
  display: inline-block;
  vertical-align: middle;
  margin-right: 8px;
}

/* Responsive */
@media (max-width: 768px) {
  .metrics-grid {
    grid-template-columns: 1fr 1fr;
  }
}
</style>